import { useSelector, useDispatch } from 'react-redux';
import { useState, useEffect, useRef } from 'react';
import { viewGameList, leaveTable, removeGame, joinGame, addChips, standUp, quickSeat, clearPendingAutoSeat, clearBuyInCancelled, openReplay, closeReplay } from '../../redux/gameSlice';
import { getBetSettings, getPlayerChips } from '../../redux/playerInfoSlice';
import { sendSocketMessage } from '../../redux/socketMiddleware';
import { fetchWithToken } from '../../utilities/fetchWithToken';
import { BASE_URL } from '../../utilities/BASE_URL';
import './gameroom.css';
import BuyInPrompt from './BuyInPrompt';
import GameTable from './GameTable';
import ReplayWindow from './ReplayWindow';

// How long the server holds a clicked seat exclusively while its buy-in prompt is open
// (mirrors PendingSitService.PENDING_SIT_TIMEOUT_SECONDS). The server is authoritative —
// it closes the prompt via BUYIN_CANCELLED on expiry; this only drives the visible countdown.
const SEAT_RESERVE_MS = 20000;

const GameRoom = () => {
    const {
      activeGames,
      primaryGameId,
      secondaryGameId,
      replays,
      mySeats,
      myCards,
      handInfo,
      pendingAutoSeat,
    } = useSelector((state) => state.games);
    const username = useSelector((state) => state.playerInfo.username);

    const dispatch = useDispatch();
    const [isSitting, setIsSitting] = useState(false);
    const [targetGameId, setTargetGameId] = useState(null);
    const [targetSeatNumber, setTargetSeatNumber] = useState(null);
    const [joinError, setJoinError] = useState(null);
    const [topUpGameId, setTopUpGameId] = useState(null);
    const [topUpError, setTopUpError] = useState(null);
    const buyInCancelled = useSelector((state) => state.games.buyInCancelled);
    // Latest isSitting, readable inside the cancellation effect without re-running it when
    // the effect itself closes the prompt (which would flip isSitting).
    const isSittingRef = useRef(isSitting);
    isSittingRef.current = isSitting;
    // Same pattern for the prompt's target, so the cancellation effect can check WHICH
    // room/seat a BUYIN_CANCELLED notice is about without re-running when it clears them.
    const targetRef = useRef({ gameId: null, seat: null });
    targetRef.current = { gameId: targetGameId, seat: targetSeatNumber };

    // Load the player's quick-select bet presets so the action bar reflects their
    // Settings → Rates Slider choices (defaults until this resolves).
    useEffect(() => {
        dispatch(getBetSettings());
    }, [dispatch]);

    // Leaderboard leader (most chips overall) — their avatar wears a gold crown.
    // Fetched once per room visit; cosmetic, so it doesn't track live chip movement.
    const [leaderUsername, setLeaderUsername] = useState(null);
    useEffect(() => {
        let cancelled = false;
        fetchWithToken(`${BASE_URL}/playerInfo/leaderboard`)
            .then((res) => res.json())
            .then((data) => {
                if (!cancelled && Array.isArray(data) && data.length > 0) {
                    setLeaderUsername(data[0].username);
                }
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const targetGame = targetGameId ? activeGames[targetGameId] : null;
    const topUpGame = topUpGameId ? activeGames[topUpGameId] : null;
    const topUpSeat = topUpGameId ? mySeats[topUpGameId] : null;
    const topUpPlayer = topUpSeat != null ? topUpGame?.players?.[topUpSeat] : null;
    const topUpCurrentStack = Number(topUpPlayer?.chips ?? 0)
      + Number(topUpPlayer?.pendingTopUp ?? 0);

    // Auto-seat from a lobby click: once the resolved table's state has landed, open the
    // buy-in prompt for the seat the backend picked (same flow as clicking an empty seat).
    useEffect(() => {
        if (!pendingAutoSeat) return;
        const { roomId, seatNumber } = pendingAutoSeat;
        if (!activeGames[roomId]) return; // wait for the table state to arrive
        if (mySeats[roomId] == null) {
            setIsSitting(true);
            setTargetGameId(roomId);
            setTargetSeatNumber(seatNumber);
            dispatch(sendSocketMessage(`/app/game/${roomId}/sitting`, { seatNumber, username }));
        }
        dispatch(clearPendingAutoSeat());
    }, [pendingAutoSeat, activeGames, mySeats, dispatch, username]);

    // The backend caps how long a seat can stay "pending" on the buy-in prompt (see
    // PendingSitService). When it releases ours, close the prompt; the popup below shows
    // why, and the seat itself is freed by the accompanying PLAYER_CANCEL_SIT broadcast.
    useEffect(() => {
        if (!buyInCancelled) return;
        // If the prompt isn't open, the timeout just raced a completed/cancelled buy-in —
        // drop the notice silently so a successful buy-in never shows a spurious popup.
        if (!isSittingRef.current) {
            dispatch(clearBuyInCancelled());
            return;
        }
        // Scope the notice to the prompt it is about: a stale reservation expiring on some
        // OTHER table/seat (e.g. an earlier quick-seat the player navigated away from) must
        // not close the prompt the player is actively buying in on.
        const { roomId, seatNumber } = buyInCancelled;
        const { gameId, seat } = targetRef.current;
        const roomMismatch = roomId != null && gameId != null && String(roomId) !== String(gameId);
        const seatMismatch = seatNumber != null && seat != null && Number(seatNumber) !== Number(seat);
        if (roomMismatch || seatMismatch) {
            dispatch(clearBuyInCancelled());
            return;
        }
        setIsSitting(false);
        setTargetGameId(null);
        setTargetSeatNumber(null);
    }, [buyInCancelled, dispatch]);

    const handleSitClick = (gameId, seatNumber) => {
        setIsSitting(true);
        setTargetGameId(gameId);
        setTargetSeatNumber(seatNumber);
        dispatch(sendSocketMessage(`/app/game/${gameId}/sitting`, { seatNumber, username }));
    };

    const handleClose = () => {
        if (targetGameId && targetSeatNumber != null) {
            dispatch(sendSocketMessage(`/app/game/${targetGameId}/cancel-sitting`, { seatNumber: targetSeatNumber }));
        }
        setIsSitting(false);
        setTargetGameId(null);
        setTargetSeatNumber(null);
    };

    const handleLeaveTable = (gameId) => {
        if (mySeats[gameId] != null) {
            dispatch(leaveTable({ roomId: gameId }));
        } else {
            dispatch(removeGame(gameId));
        }
    };

    // Stand Up: give up the seat but stay at the table as a spectator. Cash-game chips
    // and Power-Ups enter the one-hour return escrow rather than being refunded immediately.
    const handleStandUp = (gameId) => {
        dispatch(standUp({ roomId: gameId }));
    };

    const handleAddChips = (gameId) => {
        setTopUpError(null);
        setTopUpGameId(gameId);
    };

    const handleOpenReplay = (gameId) => {
        dispatch(openReplay(gameId));
    };

    const handleTopUpClose = () => {
        setTopUpError(null);
        setTopUpGameId(null);
    };

    const handleTopUpSubmit = async (targetStack) => {
        if (!topUpGameId) return;
        setTopUpError(null);
        try {
            await dispatch(addChips({ roomId: topUpGameId, targetStack })).unwrap();
            dispatch(getPlayerChips());
            handleTopUpClose();
        } catch (error) {
            setTopUpError(
              typeof error === 'string' && error
                ? error
                : 'Failed to add chips. Please try again.'
            );
        }
    };

    const handleBuyInSubmit = async (buyInAmount) => {
        if (!targetGameId) return;
        setIsSitting(false);
        setJoinError(null);
        const gameId = targetGameId;
        const seatNumber = targetSeatNumber;
        try {
            await dispatch(joinGame({
                roomId: gameId,
                buyIn: buyInAmount,
                seatNumber
            })).unwrap();
            setTargetGameId(null);
            setTargetSeatNumber(null);
        } catch (error) {
            dispatch(sendSocketMessage(`/app/game/${gameId}/cancel-sitting`, { seatNumber }));
            setTargetGameId(null);
            setTargetSeatNumber(null);
            const msg = typeof error === 'string' ? error : '';
            // The whole table filled up while the buy-in prompt was open (the backend already
            // falls back to another open seat, so only a genuinely full table lands here).
            // A rooms-list click must still end in a seat: re-resolve across the lobby group —
            // the backend spins up a fresh overflow table if everything is full — and buy
            // straight in with the amount the player already chose.
            if (/table is full/i.test(msg)) {
                const groupId = activeGames[gameId]?.groupId ?? gameId;
                try {
                    await dispatch(quickSeat({ parentRoomId: groupId, autoJoinBuyIn: buyInAmount })).unwrap();
                    return; // seated at the re-resolved table
                } catch (retryError) {
                    setJoinError(typeof retryError === 'string' ? retryError : 'Failed to join. Please try again.');
                    return;
                }
            }
            setJoinError(msg || 'Failed to join. Please try again.');
        }
    };

    // Open tables in order (primary, then secondary), then each open table's replay window. One
    // replay per table (keyed by roomId), so screens is at most 2 tables + 2 replays = 4.
    const tableIds = [primaryGameId, secondaryGameId].filter((id) => id != null);
    const twoGamesActive = tableIds.length >= 2;
    const replayIds = tableIds.filter((id) => replays[id]);
    const screens = [
      ...tableIds.map((id) => ({ kind: 'table', roomId: id })),
      ...replayIds.map((id) => ({ kind: 'replay', roomId: id })),
    ];
    const screenCount = screens.length;
    // 1-2 screens keep the existing single / side-by-side flex layout untouched; 3-4 use a 2x2
    // quadrant grid (TL, TR, BL, BR — bottom-right empty at 3).
    const quad = screenCount >= 3;

    const renderScreen = (screen) => {
      if (screen.kind === 'replay') {
        return (
          <ReplayWindow roomId={screen.roomId} onExit={() => dispatch(closeReplay(screen.roomId))} />
        );
      }
      const gameId = screen.roomId;
      return (
        <GameTable
          gameId={gameId}
          game={activeGames[gameId]}
          onSitClick={handleSitClick}
          onLeaveTable={handleLeaveTable}
          onStandUp={handleStandUp}
          onAddChips={handleAddChips}
          onAddGame={twoGamesActive ? null : () => dispatch(viewGameList())}
          onReplay={handleOpenReplay}
          replayOpen={replays[gameId] != null}
          pendingSeat={targetGameId === gameId ? targetSeatNumber : null}
          pendingUsername={username}
          isSeated={mySeats[gameId] != null}
          myCards={myCards[gameId]}
          handInfo={handInfo[gameId]}
          mySeat={mySeats[gameId]}
          leaderUsername={leaderUsername}
        />
      );
    };

    return (
      <div className={`game-room ${screenCount === 2 ? 'split-screen' : ''} ${quad ? 'quad-screen' : ''}`}>
        <div className={`games-container ${quad ? 'quad' : ''}`}>
          {screens.map((screen, i) => {
            // Preserve the exact 2-table classes for 1-2 screens; use a uniform grid cell for 3-4.
            const cellClass = quad
              ? 'game-screen-cell'
              : (i === 0
                  ? `primary-game ${screenCount === 2 ? 'split' : ''}`
                  : 'secondary-game split');
            return (
              <div key={`${screen.kind}-${screen.roomId}`} className={cellClass}>
                {renderScreen(screen)}
              </div>
            );
          })}
        </div>

        {joinError && (
          <div className="join-error">{joinError}</div>
        )}

        {isSitting && targetGame?.minBuyIn && (
          <BuyInPrompt
            key={`${targetGameId}:${targetSeatNumber}`}
            onClose={handleClose}
            onSubmit={handleBuyInSubmit}
            minBuy={targetGame.minBuyIn}
            maxBuy={targetGame.maxBuyIn}
            returnMinBuyIn={targetGame.returnMinBuyIn}
            returnExpiresAt={targetGame.returnExpiresAt}
            savedPupCards={targetGame.savedPupCards}
            reserveMs={SEAT_RESERVE_MS}
          />
        )}

        {topUpGame && topUpPlayer && (
          <BuyInPrompt
            mode="topUp"
            onClose={handleTopUpClose}
            onSubmit={handleTopUpSubmit}
            minBuy={topUpCurrentStack}
            maxBuy={topUpGame.maxBuyIn}
            currentStack={topUpCurrentStack}
            error={topUpError}
          />
        )}

        {buyInCancelled && (
          <div className="inactivity-overlay">
            <div className="inactivity-box">
              <h3>Buy-In Cancelled</h3>
              <p>{buyInCancelled.message || 'Your buy-in has been cancelled due to inactivity.'}</p>
              <button type="button" onClick={() => dispatch(clearBuyInCancelled())}>OK</button>
            </div>
          </div>
        )}
      </div>
    );
};

export default GameRoom;


/*
{buttons.map(button => (
        <button
          key={button.id}
          className="dynamic-button"
          style={{
            position: 'absolute',
            bottom: `${button.bottom}px`,
            left: `${button.left}%`,
            transform: 'translateX(-50%)'
          }}
*/
